import { useIsEditorPage } from '@confluence/route-manager/entry-points/useIsEditorPage';
import { useIsCurrentPageLive } from '@confluence/live-pages-utils/entry-points/useIsCurrentPageLive';
import { useRouteName } from '@confluence/route-manager/entry-points/RouteState';
import { fg } from '@confluence/feature-gating';
import { isDatabase, isWhiteboard } from '@confluence/named-routes/entry-points/routes-utils';

/**
 * WARNING: If this ends up running on a page route which is not for either a Live Doc, or Page (edit or published view),
 * this will return 'renderer' which is the fall back.
 *
 * If running on a non page or doc route
 * - if it is a database or whiteboard: will return 'not-page-or-doc'.
 * - otherwise: it will return 'renderer'.
 *
 * This is only suitable for comment purposes and the fall back to 'renderer' may not suit
 * use cases outside of commenting (where for the comment experiences -- they are only expected
 * to run on pages, live docs, whiteboards, and databases).
 */
export function usePageClass(): 'editor' | 'renderer' | 'not-page-or-doc' {
	// Note -- this hook does not inform the consumer if they are on a live doc page which
	// is using the editor.
	const isEditorPage = useIsEditorPage();
	const isCurrentPageLive = useIsCurrentPageLive();
	// useRouteName use RoutesContext which is more reliable than reading window.location.pathname directly.
	const routeName = useRouteName({ selector: (name) => name });

	const isTinyMCEPage = __checkIfTinyMCEEditorPage();
	if (isTinyMCEPage) {
		// TinyMCE pages are not considered editor pages or docs in the context of this hook
		return 'not-page-or-doc';
	}

	if (isEditorPage) {
		return 'editor';
	}

	if (isCurrentPageLive) {
		return 'editor';
	}

	if (fg('confluence_frontend_fix_page_class_route_detection')) {
		// Pass routeName to isDatabase/isWhiteboard - they will use it when feature gate is enabled
		if (isDatabase(routeName) || isWhiteboard(routeName)) {
			return 'not-page-or-doc';
		}
	} else {
		// Legacy behavior: use window.location.pathname (can have race conditions during SPA transitions)
		if (isDatabase() || isWhiteboard()) {
			return 'not-page-or-doc';
		}
	}

	return 'renderer';
}

function __checkIfTinyMCEEditorPage(): boolean {
	const possibleTinyMCESelectors = [`[data-testid="TinyMCERendererTestId"]`, '.tinymce-editor'];

	// This check is only relevant in the context of comment experiences
	// and so if we are in an environment without a document -- we
	// can safely treat it as not a tinymce page.
	if (typeof document === 'undefined') {
		return false;
	}

	const isTinyMCEPage = possibleTinyMCESelectors.some(
		(selector) => document.querySelector(selector) !== null,
	);

	return isTinyMCEPage;
}
